import {
  Box,
  Divider,
  Flex,
  Icon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import React, { ChangeEvent, useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import AutomationDomain, {
  TimeOperation,
  TimeUnit,
} from "entities/domain/automation";
import { Color } from "theme/old-design-system/styled-components";
import { ReactComponent as Alarm } from "assets/icons/alarm.svg";
import { ReactComponent as Checkmark } from "assets/icons/Correct-ds.svg";
import { ReactComponent as TrashBin } from "assets/icons/bin-ds.svg";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import { getReactSelectStyles } from "util/methods";

interface SelectOption {
  value: string;
  label: string;
}

interface WaitUntilProps {
  automation: AutomationDomain;
  setUpdatedAutomation: (automation: AutomationDomain) => void;
}

const WaitUntil = ({ automation, setUpdatedAutomation }: WaitUntilProps) => {
  const { colorScheme } = useCustomChakraTheme();
  const { colorMode } = useColorMode();
  const [timeOperation, setTimeOperation] = useState<TimeOperation | undefined>(
    automation?.time?.operation
  );
  const [timeAmount, setTimeAmount] = useState<number | undefined>(
    automation?.time?.amount
  );
  const [timeUnit, setTimeUnit] = useState<TimeUnit | undefined>(
    automation?.time?.unit
  );

  useEffect(() => {
    const time =
      !timeOperation && !timeAmount && !timeUnit
        ? null
        : {
            operation: timeOperation,
            amount: timeAmount,
            unit: timeUnit,
          };
    if (timeOperation && typeof timeAmount !== "undefined" && timeUnit) {
      setUpdatedAutomation(
        new AutomationDomain(
          automation.id,
          automation.name,
          automation.description,
          automation.merchantId,
          automation.triggerType,
          !timeOperation && !timeAmount && !timeUnit
            ? null
            : {
                ...automation.time,
                operation: timeOperation,
                amount: timeAmount,
                unit: timeUnit,
              },
          automation.frequency,
          automation.task,
          automation.templateId,
          automation.enabled
        )
      );
    }
  }, [timeOperation, timeAmount, timeUnit]);

  let text = "Instantly";

  if (timeOperation && timeAmount && timeUnit) {
    const time = `${timeAmount} ${timeAmount > 1 ? `${timeUnit}s` : timeUnit}`;

    text = `${time} ${timeOperation}`;
  }

  const [isEditingExecutionTime, setIsEditingExecutionTime] =
    useState<boolean>(false);

  const timePeriodOptions: SelectOption[] = [
    {
      label: "Before",
      value: "before",
    },
    {
      label: "After",
      value: "after",
    },
  ].filter((periodOption) =>
    automation
      .getAllowedTimeOperations()
      .includes(periodOption.value as TimeOperation)
  );

  const timeUnitOptions: SelectOption[] = [
    {
      label: "Second",
      value: "second",
    },
    {
      label: "Minute",
      value: "minute",
    },
    {
      label: "Hour",
      value: "hour",
    },
    {
      label: "Day",
      value: "day",
    },
    {
      label: "Week",
      value: "week",
    },
  ].filter((unitOption) =>
    automation.getAllowedTimeUnits().includes(unitOption.value as TimeUnit)
  );

  const [selectedTimePeriodValue, setSelectedTimePeriodValue] = useState<
    SingleValue<SelectOption>
  >(
    !timeOperation
      ? null
      : timePeriodOptions.find(({ value }) => value === timeOperation) || null
  );

  const [selectedTimeUnitValue, setSelectedTimeUnitValue] = useState<
    SingleValue<SelectOption>
  >(
    !timeUnit
      ? null
      : timeUnitOptions.find(({ value }) => value === timeUnit) || null
  );

  const handleTimePeriodChange = (e: SingleValue<SelectOption>) => {
    setTimeOperation(e?.value as TimeOperation);
    setSelectedTimePeriodValue(e);
  };

  const handleTimeAmountChange = (
    _valueAsString: string,
    valueAsNumber: number
  ) => {
    setTimeAmount(valueAsNumber);
  };

  const handleTimeUnitChange = (e: SingleValue<SelectOption>) => {
    setTimeUnit(e?.value as TimeUnit);
    setSelectedTimeUnitValue(e);
  };

  useEffect(() => {
    if (timeOperation && timeAmount && timeUnit) {
      setUpdatedAutomation(
        new AutomationDomain(
          automation.id,
          automation.name,
          automation.description,
          automation.merchantId,
          automation.triggerType,
          {
            ...automation.time,
            operation: timeOperation,
            amount: timeAmount,
            unit: timeUnit,
          },
          automation.frequency,
          automation.task,
          automation.templateId,
          automation.enabled
        )
      );
    }
  }, [timeOperation, timeAmount, timeUnit]);

  return (
    <>
      <Box px={6} my={5}>
        <Text fontWeight={700} pb="1rem">
          Send when:
        </Text>
        <Flex alignItems="center">
          <Icon
            as={Alarm}
            mr="1rem"
            fill={`${colorScheme}.600`}
            __css={{
              height: "30px",
              width: "30px",
            }}
          />
          {!isEditingExecutionTime ? (
            <Flex
              borderRadius="full"
              border={`1px solid ${Color.LIGHT_GREY.value}`}
              height="2.5rem"
              px="1rem"
              onClick={() => setIsEditingExecutionTime(true)}
              _hover={{
                cursor: "pointer",
                boxShadow: `0 0 1rem ${Color.GREYISH_WHITE.value}`,
                border: "none",
                color: "white",
                backgroundColor: `${colorScheme}.600`,
              }}
              alignItems="center"
            >
              <Text
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                textAlign="center"
              >
                {text}
              </Text>
            </Flex>
          ) : (
            <Flex
              wrap="nowrap"
              justifyContent="space-evenly"
              alignItems="center"
              width="100%"
            >
              <Flex direction="column" gridGap={3}>
                <NumberInput
                  data-testid="input-time-amount"
                  step={1}
                  defaultValue={timeAmount || 0}
                  min={0}
                  max={30}
                  onChange={handleTimeAmountChange}
                >
                  <NumberInputField value={timeAmount} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Select
                  id="select-time-unit"
                  placeholder="Select time unit"
                  onChange={handleTimeUnitChange}
                  options={timeUnitOptions}
                  value={selectedTimeUnitValue}
                  styles={getReactSelectStyles(colorMode, colorScheme)}
                />
                <Select
                  id="select-time-period"
                  placeholder="Select time period"
                  onChange={handleTimePeriodChange}
                  options={timePeriodOptions}
                  value={selectedTimePeriodValue}
                  styles={getReactSelectStyles(colorMode, colorScheme)}
                />
              </Flex>
              <Icon
                as={TrashBin}
                onClick={() => {
                  setTimeAmount(0);
                  setTimeUnit(undefined);
                  setTimeOperation(undefined);
                  setIsEditingExecutionTime(false);
                  setUpdatedAutomation(
                    new AutomationDomain(
                      automation.id,
                      automation.name,
                      automation.description,
                      automation.merchantId,
                      automation.triggerType,
                      null,
                      automation.frequency,
                      automation.task,
                      automation.templateId,
                      automation.enabled
                    )
                  );
                }}
                __css={{
                  cursor: "pointer",
                  height: "20px",
                  width: "20px",
                  path: {
                    fill: Color.SMOKY_GREY.value,
                  },
                }}
              />
              <Icon
                as={Checkmark}
                onClick={() => setIsEditingExecutionTime(false)}
                __css={{
                  cursor: "pointer",
                  height: "20px",
                  width: "20px",
                  path: {
                    fill: Color.SECONDARY_EMERALD_GREEN.value,
                  },
                }}
              />
            </Flex>
          )}
        </Flex>
      </Box>
      <Divider />
    </>
  );
};

export default WaitUntil;
